import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import { Form, Formik, FormikHelpers } from 'formik';
import { Box, Button, IconButton, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import AddIcon from '@mui/icons-material/Add';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import { Text, yup } from '@app/formik';
import { Modal } from '@app/ui/modal';
import { Tooltip } from '@app/ui/tooltip';
import { error as toastError, success as toastSuccess } from '@app/snackbars';

import { COLOR_BANDS_REPLACE } from '../../gql';
import { ColorBand, ColorBandInput, ColorBandsReplaceInput, ColorCodeColor } from '../../types';
import { COLOR_CODE_COLOR_VALUES } from '../../constants';
import { getGraphQLErrorMessage, nextRowUid } from '../../utils';

export interface ColorBandsEditorProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly ruleSetVersionId: number;
  readonly bands: ColorBand[];
  readonly onSaved: () => Promise<unknown> | void;
}

interface BandFormValue {
  uid: string;
  min: string;
  max: string;
  color: ColorCodeColor;
}

interface FormValues {
  bands: BandFormValue[];
}

const emptyBand = (): BandFormValue => ({ uid: nextRowUid('band'), min: '', max: '', color: ColorCodeColor.green });

const toBandRow = (band: ColorBand): BandFormValue => ({
  uid: nextRowUid('band'),
  min: band.min != null ? String(band.min) : '',
  max: band.max != null ? String(band.max) : '',
  color: band.color,
});

// Formik hands yup `undefined` for every `''`, so an open-ended band's blank side is not a string here.
const asText = (value: string | undefined): string => (typeof value === 'string' ? value : '');
const isIntegerOrBlank = (value: string | undefined): boolean =>
  !asText(value).trim() || Number.isInteger(Number(value));

// Bands are a SET, not individually-editable rows — replace-all is the honest op (PLAN §13): D4's
// exhaustive/non-overlapping check runs at PUBLISH (`colorRubricPreviewGetOne`, rendered by
// `ColorRubricPanel` above this dialog), never here, so a mid-edit intermediate state is never
// blocked from being saved.
export const ColorBandsEditor = ({ open, onClose, ruleSetVersionId, bands, onSaved }: ColorBandsEditorProps) => {
  const { t } = useTranslation('common');
  const translate = (key: string) => t(key) ?? key;

  const [replaceBands, { loading }] = useMutation<{ colorBandsReplace: ColorBand[] }>(COLOR_BANDS_REPLACE);

  const initialValues = useMemo<FormValues>(
    () => ({ bands: bands.length ? bands.map(toBandRow) : [emptyBand()] }),
    [bands],
  );

  const validationSchema = useMemo(
    () =>
      yup.object().shape({
        bands: yup.array().test({
          name: 'bandRows',
          test(rows, context) {
            const bandRows = (rows ?? []) as BandFormValue[];

            if (!bandRows.length) {
              return true;
            }

            const valid = bandRows.every(
              row =>
                (!!asText(row.min).trim() || !!asText(row.max).trim()) &&
                isIntegerOrBlank(row.min) &&
                isIntegerOrBlank(row.max),
            );

            return valid
              ? true
              : context.createError({ message: translate('rules.colorCoding.bands.validation.invalidRow') });
          },
        }),
      }),
    [t],
  );

  const handleSubmit = async (values: FormValues, helpers: FormikHelpers<FormValues>) => {
    const input: ColorBandsReplaceInput = {
      ruleSetVersionId,
      bands: values.bands.map(
        (row): ColorBandInput => ({
          ...(row.min.trim() ? { min: Number(row.min) } : {}),
          ...(row.max.trim() ? { max: Number(row.max) } : {}),
          color: row.color,
        }),
      ),
    };

    try {
      await replaceBands({ variables: { input } });
      toastSuccess(t('rules.colorCoding.bands.savedMessage'));
      await onSaved();
      onClose();
    } catch (e) {
      toastError(getGraphQLErrorMessage(e) ?? t('general.error.somethingWentWrong'));
      helpers.setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      maxWidth="md"
      disableBackdropClose
      title={t('rules.colorCoding.bands.dialogTitle')}
    >
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('rules.colorCoding.bands.intro')}
      </Typography>

      <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
        {({ values, errors, submitCount, isSubmitting, setFieldValue }) => {
          const rows = values.bands;
          const replaceRows = (next: BandFormValue[]) => setFieldValue('bands', next);
          const handleMove = (index: number, direction: -1 | 1) => {
            const target = index + direction;

            if (target < 0 || target >= rows.length) {
              return;
            }

            const next = [...rows];

            [next[index], next[target]] = [next[target], next[index]];
            replaceRows(next);
          };

          return (
            <Form noValidate>
              {rows.map((row, index) => (
                <Box
                  key={row.uid}
                  sx={{ border: '1px solid', borderColor: 'grey.300', borderRadius: '16px', p: 2, mb: 2 }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="h4">
                      {t('rules.colorCoding.bands.rowHeading', { number: index + 1 })}
                    </Typography>

                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Tooltip title={t('rules.editor.condition.moveUpButton') ?? undefined}>
                        <span>
                          <IconButton size="small" disabled={index === 0} onClick={() => handleMove(index, -1)}>
                            <ArrowUpwardIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title={t('rules.editor.condition.moveDownButton') ?? undefined}>
                        <span>
                          <IconButton
                            size="small"
                            disabled={index === rows.length - 1}
                            onClick={() => handleMove(index, 1)}
                          >
                            <ArrowDownwardIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title={t('rules.editor.condition.removeButton') ?? undefined}>
                        <span>
                          <IconButton
                            size="small"
                            disabled={rows.length <= 1}
                            onClick={() => replaceRows(rows.filter((_row, rowIndex) => rowIndex !== index))}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: 2 }}>
                    <Box sx={{ width: { xs: '100%', sm: 140 } }}>
                      <Text
                        name={`bands.${index}.min`}
                        label={t('rules.colorCoding.bands.field.min')}
                        helperText={t('rules.colorCoding.bands.field.openHelperText')}
                      />
                    </Box>
                    <Box sx={{ width: { xs: '100%', sm: 140 } }}>
                      <Text
                        name={`bands.${index}.max`}
                        label={t('rules.colorCoding.bands.field.max')}
                        helperText={t('rules.colorCoding.bands.field.openHelperText')}
                      />
                    </Box>
                    <Box sx={{ flexGrow: 1, minWidth: 160 }}>
                      <Text
                        select
                        name={`bands.${index}.color`}
                        label={t('rules.colorCoding.bands.field.color')}
                        options={COLOR_CODE_COLOR_VALUES.map(value => ({
                          id: value,
                          name: t(`rules.colorCoding.color.option.${value}`) ?? value,
                        }))}
                      />
                    </Box>
                  </Box>
                </Box>
              ))}

              <Button size="small" startIcon={<AddIcon />} onClick={() => replaceRows([...rows, emptyBand()])}>
                {t('rules.colorCoding.bands.addButton')}
              </Button>

              {/* The `bands` array test is whole-set, not per-row, so there is no `ColorCaseRow`-style
              per-row Typography to fall back on (unlike the scoring/gate editors) — without this the
              Save button just does nothing, with no explanation. */}
              {submitCount > 0 && typeof errors.bands === 'string' && (
                <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                  {errors.bands}
                </Typography>
              )}

              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mt: 3 }}>
                <Button onClick={onClose}>{t('rules.editor.cancelButton')}</Button>
                <LoadingButton loading={isSubmitting || loading} variant="contained" color="primary" type="submit">
                  {t('rules.colorCoding.saveButton')}
                </LoadingButton>
              </Box>
            </Form>
          );
        }}
      </Formik>
    </Modal>
  );
};
